import type { FileUpload } from '../types';

/**
 * Extract text content from various file types
 * Supports: PDF, HTML, Image (OCR), Text
 */
export async function processFile(file: File): Promise<FileUpload> {
  const upload: FileUpload = {
    id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: file.name,
    type: getFileType(file),
    content: '',
    processed: false,
  };

  try {
    switch (upload.type) {
      case 'pdf':
        upload.content = await extractFromPDF(file);
        break;
      case 'html':
        upload.content = await extractFromHTML(file);
        break;
      case 'image':
        upload.content = await extractFromImage(file);
        break;
      case 'text':
        upload.content = await readTextFile(file);
        break;
      default:
        throw new Error(`Unsupported file type: ${file.type}`);
    }
    upload.processed = true;
  } catch (error) {
    upload.processed = false;
    throw new Error(`Failed to process ${file.name}: ${(error as Error).message}`);
  }

  return upload;
}

/**
 * Determine file type from File object
 */
function getFileType(file: File): FileUpload['type'] {
  const type = file.type.toLowerCase();
  if (type.includes('pdf')) return 'pdf';
  if (type.includes('html') || type.includes('htm')) return 'html';
  if (type.startsWith('image/')) return 'image';
  if (type.includes('text') || type.includes('plain') || file.name.endsWith('.txt')) return 'text';
  if (file.name.endsWith('.docx') || type.includes('wordprocessingml')) return 'text'; // treat docx as text (simplified)
  return 'text';
}

/**
 * Extract text from PDF using text extraction
 */
async function extractFromPDF(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        const text = extractTextFromPDF(uint8Array);
        resolve(text || 'PDF content extracted (raw binary data)');
      } catch (error) {
        reject(new Error('Failed to extract text from PDF'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read PDF file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Simple PDF text extraction from raw bytes
 * This is a basic implementation - in production, use pdf-lib or pdf-parse
 */
function extractTextFromPDF(uint8Array: Uint8Array): string {
  const decoder = new TextDecoder('utf-8');
  const text = decoder.decode(uint8Array);

  // Extract text between BT (Begin Text) and ET (End Text) markers
  const textChunks: string[] = [];
  const regex = /\[([^\]]*)\] TJ/g; // Basic text extraction from PDF streams

  let match;
  while ((match = regex.exec(text)) !== null) {
    textChunks.push(match[1].replace(/\\\(/g, '(').replace(/\\\)/g, ')'));
  }

  // Fallback: try to extract readable text from raw bytes
  if (textChunks.length === 0) {
    const readable = text
      .replace(/[^\x20-\x7E\n]/g, ' ') // Remove non-printable chars
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 10000);
    return readable || 'Unable to extract text from PDF. Please provide text content directly.';
  }

  return textChunks.join(' ').replace(/\s+/g, ' ').trim() || 'PDF processing pending - text extraction may be limited.';
}

/**
 * Extract text from HTML file
 */
async function extractFromHTML(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const html = reader.result as string;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Remove script and style elements
        doc.querySelectorAll('script, style, nav, header, footer, aside').forEach((el) => el.remove());

        const text = doc.body?.textContent || '';
        resolve(text.replace(/\s+/g, ' ').trim());
      } catch (error) {
        reject(new Error('Failed to parse HTML'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read HTML file'));
    reader.readAsText(file);
  });
}

/**
 * Extract text from an image using OCR
 * Note: In browser, we use a fallback approach. For true OCR, use Tesseract.js or API
 */
async function extractFromImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        // For images, we return a placeholder indicating image processing
        // In production, use Tesseract.js for client-side OCR or an API
        resolve(`IMAGE-[${file.name}]

Note: Image OCR requires client-side processing. The image has been queued for processing.
For text extraction from images, please use an OCR service or provide the text content directly.

Image details:
- Name: ${file.name}
- Size: ${(file.size / 1024).toFixed(2)} KB
- Type: ${file.type}`);
      } catch (error) {
        reject(new Error('Failed to process image'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Read plain text file
 */
async function readTextFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).replace(/\s+/g, ' ').trim());
    reader.onerror = () => reject(new Error('Failed to read text file'));
    reader.readAsText(file);
  });
}

/**
 * Combine multiple file contents into a single text block
 */
export function combineFileContents(uploads: FileUpload[]): string {
  return uploads
    .map((upload) => `--- ${upload.name} ---\n${upload.content}`)
    .join('\n\n');
}

/**
 * Get file extension
 */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

/**
 * Check if file format is supported
 */
export function isSupportedFile(file: File): boolean {
  const supportedTypes = [
    'application/pdf',
    'text/html',
    'text/plain',
    'text/markdown',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
  ];
  const supportedExtensions = ['.pdf', '.txt', '.md', '.html', '.htm', '.png', '.jpg', '.jpeg', '.webp', '.gif'];

  if (supportedTypes.includes(file.type)) return true;
  return supportedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export const fileProcessor = {
  processFile,
  combineFileContents,
  getFileExtension,
  isSupportedFile,
  formatFileSize,
};

export default fileProcessor;
